import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { 
  FaArrowLeft, 
  FaCalendarAlt, 
  FaClock, 
  FaUser, 
  FaBuilding, 
  FaCheckCircle, 
  FaQuoteLeft, 
  FaArrowRight,
  FaSearch,
  FaEnvelope,
  FaInfoCircle,
  FaShieldAlt,
  FaLeaf,
  FaUserShield,
  FaGavel,
  FaCogs,
  FaShip,
  FaHospital,
  FaIndustry,
  FaSchool
} from "react-icons/fa";
import "../Style/CertificationsDetail.css";
import frameworks from "./data.json"; // Update this path to match your data.json location
import heroVideo from "../assets/make_a_video_this.mp4"; // Optional hero video asset

function CertificationsListAndDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const categories = ["All", "Ops", "Green", "Safety", "Secure", "Rule"];

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [id]);

  const handleSubscribe = (e) => {
    e.preventDefault();
    alert(`Subscribed successfully: ${emailInput}`);
    setEmailInput("");
  };

  const resetPagination = () => setCurrentPage(1);

  // Helper icon selector for industries
  const getIndustryIcon = (name) => {
    const lower = name.toLowerCase();
    if (lower.includes('resort') || lower.includes('marine') || lower.includes('liveaboard') || lower.includes('transport') || lower.includes('ship')) return <FaShip />;
    if (lower.includes('health') || lower.includes('clinic') || lower.includes('hospital')) return <FaHospital />;
    if (lower.includes('construct') || lower.includes('industr') || lower.includes('infrastructure') || lower.includes('supply')) return <FaIndustry />;
    if (lower.includes('educat') || lower.includes('school') || lower.includes('institut')) return <FaSchool />;
    return <FaBuilding />;
  };

  // ==========================================
  // 1. DEDICATED SINGLE CERTIFICATION DETAIL PAGE (`/certifications/:id`)
  // ==========================================
  if (id) {
    const certification = frameworks.find((f) => f.id === id);

    if (!certification) {
      return (
        <div className="newspaper-detail-layout">
          <div className="detail-error-container" style={{ textAlign: "center", padding: "100px 20px" }}>
            <FaInfoCircle className="error-icon" style={{ fontSize: "3rem", color: "#b91c1c", marginBottom: "20px" }} />
            <h2>Standard Not Found</h2>
            <p>The standard or certification requested is missing from the active compliance index.</p>
            <Link to="/certifications" className="back-button" style={{ display: "inline-flex", alignItems: "center", gap: "8px", marginTop: "20px", padding: "10px 20px", background: "#1e3a8a", color: "#ffffff", borderRadius: "6px", textDecoration: "none" }}>
              <FaArrowLeft /> Return to Standards Index
            </Link>
          </div>
        </div>
      );
    }

    const relatedCerts = certification?.related 
      ? frameworks.filter((f) => certification.related.includes(f.id)) 
      : frameworks.filter((f) => f.id !== certification.id).slice(0, 3);

    return (
      <div className="newspaper-detail-layout base-fade-in">
        <div className="newspaper-top-border">
          <div className="border-pattern"></div>
        </div>

        <header className="newspaper-masthead">
          <div className="masthead-container">
            <div className="publication-branding">
              <FaShieldAlt className="brand-icon" />
              <span>ISO Compliance Guide - Maldives</span>
            </div>
            <Link to="/certifications" className="masthead-back-btn">
              <FaArrowLeft /> Return to Standards Index
            </Link>
            <div className="publication-meta">
              <span className="meta-item">
                <FaCalendarAlt /> {certification.date || "July 2026"}
              </span>
              <span className="meta-item">
                <FaClock /> {certification.readTime || "5 min read"}
              </span>
            </div>
          </div>
        </header>

        {/* Hero Banner Section */}
        <div className="headline-section centered-hero">
          <div className="headline-container">
            <div className="headline-badge-group centered-badges">
              <span className="status-pill">Active Compliance Standard</span>
            </div>
            <h1 className="newspaper-headline">{certification.title}</h1>
            <h2 className="newspaper-subheadline">{certification.name}</h2>
            <p className="lead-paragraph centered-lead">{certification.purpose}</p>
            <div className="hero-cta-group" style={{ marginTop: '25px', display: 'flex', justifyContent: 'center' }}>
              <a href="#contact-form" className="primary-action-btn">
                <FaCheckCircle /> Get Certified Today
              </a>
            </div>
          </div>
        </div>

        <main className="newspaper-main-content">
          {/* SECTION 1: ABOUT & BENEFITS */}
          <div className="content-row bg-white-section">
            <div className="content-row-image">
              <div className="image-collage-grid">
                <div className="collage-main-wrapper">
                  <img src={certification.mainImage} alt={`${certification.title} Operational View`} className="newspaper-main-image" />
                  <div className="image-credit-line">
                    <FaBuilding /> {certification.title} Implementation View
                  </div>
                </div>
                {certification.secondImage && (
                  <div className="collage-sub-wrapper">
                    <img src={certification.secondImage} alt={`${certification.title} Quality Review`} className="newspaper-sub-image" />
                  </div>
                )}
              </div>

              {relatedCerts.length > 0 && (
                <div className="related-sidebar-box">
                  <div className="sidebar-header">
                    <FaBuilding /> Complementary Standards
                  </div>
                  <div className="related-list">
                    {relatedCerts.map((related) => (
                      <Link key={related.id} to={`/certifications/${related.id}`} className="related-item" style={{ textDecoration: "none" }}>
                        <span className="related-code">{related.title}</span>
                        <span className="related-name">{related.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="content-row-details">
              <article className="main-article">
                <div className="section-header-centered">
                  <h3>About the Standard</h3>
                  <div className="divider-line"></div>
                </div>
                <div className="article-body">
                  <p className="article-paragraph first-paragraph">
                    <span className="drop-cap">{certification.description.charAt(0)}</span>
                    {certification.description.slice(1)}
                  </p>
                  <p className="article-paragraph">
                    Implementing {certification.title} across Maldivian enterprises builds robust operational resilience, satisfies strict local and international regulatory frameworks, and protects local community values.
                  </p>
                  
                  {certification.insight && (
                    <div className="pull-quote">
                      <FaQuoteLeft className="quote-mark" />
                      <blockquote>
                        "{certification.insight}"
                      </blockquote>
                    </div>
                  )}
                </div>
              </article>

              {certification.benefits && certification.benefits.length > 0 && (
                <section className="article-section">
                  <div className="section-header-centered">
                    <h3>Key Advantages &amp; Benefits</h3>
                    <div className="divider-line"></div>
                  </div>
                  <div className="benefits-columns">
                    {certification.benefits.map((benefit, index) => (
                      <div key={index} className="benefit-item">
                        <FaCheckCircle className="benefit-check" />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>

          {/* SECTION 2: REQUIREMENTS & INDUSTRIES */}
          <div className="content-row reversed-row bg-tinted-section">
            <div className="content-row-details">
              {certification.requirements && certification.requirements.length > 0 && (
                <section className="article-section">
                  <div className="section-header-centered">
                    <h3>Key Requirements</h3>
                    <div className="divider-line"></div>
                  </div>
                  <div className="requirements-list-newspaper">
                    {certification.requirements.map((req, index) => (
                      <div key={index} className="req-item">
                        <div className="req-bullet">{index + 1}</div>
                        <div className="req-content">{req}</div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {certification.industries && (
                <section className="article-section industries-newspaper-section">
                  <div className="section-header-centered">
                    <h3>Industries Served</h3>
                    <div className="divider-line"></div>
                  </div>
                  <div className="industries-modern-grid">
                    {certification.industries.split(', ').map((industry, index) => {
                      const trimmed = industry.trim();
                      return (
                        <div key={index} className="industry-card-item">
                          <div className="industry-card-icon-box">
                            {getIndustryIcon(trimmed)}
                          </div>
                          <div className="industry-card-text">
                            <h4>{trimmed}</h4>
                            <p>Optimized compliance frameworks tailored for regional atoll operations.</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}
            </div>

            <div className="content-row-image">
              <div className="image-collage-grid">
                {certification.thirdImage && (
                  <div className="collage-main-wrapper">
                    <img src={certification.thirdImage} alt={`${certification.title} Analysis`} className="newspaper-main-image" />
                    <div className="image-credit-line">
                      <FaBuilding /> Operational Execution View
                    </div>
                  </div>
                )}
                {certification.fourthImage && (
                  <div className="collage-sub-wrapper">
                    <img src={certification.fourthImage} alt={`${certification.title} Process Control`} className="newspaper-sub-image" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* SECTION 4: FAQS & QUOTE FORM */}
          <div className="content-row bg-white-section">
            <div className="content-row-details full-width-details">
              {certification.faqs && certification.faqs.length > 0 && (
                <section className="article-section">
                  <div className="section-header-centered">
                    <h3>Frequently Asked Questions</h3>
                    <div className="divider-line"></div>
                  </div>
                  <div className="faqs-list-container">
                    {certification.faqs.map((faq, idx) => (
                      <div key={idx} className="faq-card-item">
                        <h4>{faq.q}</h4>
                        <p>{faq.a}</p>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* CTA SECTION */}
              <section id="contact-form" className="cta-newsletter-box">
                <div className="cta-content">
                  <h3>Request a Free Consultation or Get a Quote</h3>
                  <p>Connect directly with our senior Maldives ISO consultants for an immediate scope evaluation and custom {certification.title} package.</p>
                  <div className="cta-contact-row">
                    <a href="tel:+919867180395" className="cta-contact-btn">
                      +91 9867180395
                    </a>
                    <a href="mailto:info@ascentinspecta.com" className="cta-contact-btn">
                      info@ascentinspecta.com
                    </a>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </main>

        <div className="newspaper-bottom-border"></div>
      </div>
    );
  }

  // ==========================================
  // 2. CERTIFICATIONS DIRECTORY / LISTING VIEW (`/certifications`)
  // ==========================================
  const filteredCertifications = frameworks.filter((item) => {
    const matchesCategory = selectedCategory === "All" || item.category === selectedCategory;
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          item.purpose.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredCert = frameworks.find((item) => item.featured) || frameworks[0];
  const regularCerts = filteredCertifications.filter((item) => !item.featured || selectedCategory !== "All" || searchQuery !== "");
  const totalPages = Math.max(1, Math.ceil(regularCerts.length / itemsPerPage));
  const currentCerts = regularCerts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div className="newspaper-detail-layout base-fade-in">
      <div className="newspaper-top-border">
        <div className="border-pattern"></div>
      </div>

      <header className="newspaper-masthead">
        <div className="masthead-container">
          <div className="publication-branding">
            <FaShieldAlt className="brand-icon" />
            <span>ISO Guide - Maldives</span>
          </div>
          <div className="publication-meta">
            <span className="meta-item">
              <FaCalendarAlt /> {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          </div>
        </div>
      </header>

      {/* Listing Hero Header */}
      <div className="headline-section centered-hero">
        <div className="headline-container">
          <div className="headline-badge-group centered-badges">
            <span className="status-pill">Standards Knowledge Registry</span>
          </div>
          <h1 className="newspaper-headline">Maldives ISO Compliance Standards</h1>
          <p className="lead-paragraph centered-lead">
            Explore international standard specifications, auditing protocols, and compliance roadmaps 
            curated for Maldivian commerce, eco-resort logistics, and regulatory frameworks.
          </p>

          <div className="search-wrapper" style={{ maxWidth: "600px", margin: "30px auto 0 auto" }}>
            <input 
              type="text" 
              placeholder="Search standards by keyword, title, or sector..." 
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                resetPagination();
              }}
              style={{ width: "100%", padding: "12px 20px", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.3)", background: "rgba(255,255,255,0.1)", color: "#fff", outline: "none" }}
            />
          </div>
        </div>
      </div>

      {/* Filter Navigation Bar */}
      <nav className="sticky-tab-nav">
        <div className="tab-nav-container centered-tabs">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`nav-tab-btn ${selectedCategory === cat ? "active" : ""}`}
              onClick={() => {
                setSelectedCategory(cat);
                resetPagination();
              }}
            >
              {cat}
            </button>
          ))}
        </div>
      </nav>

      {/* Main Listing Grid Area */}
      <main className="newspaper-main-content">
        {selectedCategory === "All" && searchQuery === "" && featuredCert && (
          <div className="content-row bg-white-section" style={{ marginBottom: "40px", cursor: "pointer" }} onClick={() => navigate(`/certifications/${featuredCert.id}`)}>
            <div className="content-row-image">
              <div className="collage-main-wrapper" style={{ height: "100%" }}>
                <img src={featuredCert.mainImage} alt={featuredCert.title} className="newspaper-main-image" style={{ height: "100%", minHeight: "320px" }} />
              </div>
            </div>
            <div className="content-row-details">
              <span className="category-ribbon" style={{ width: "fit-content" }}>Featured Standard</span>
              <h2 style={{ fontSize: "2rem", color: "#1e3a8a", margin: "0 0 10px 0" }}>{featuredCert.title}: {featuredCert.name}</h2>
              <p style={{ color: "#475569", lineHeight: "1.6", fontSize: "1.05rem" }}>{featuredCert.purpose}</p>
              <div>
                <span className="primary-action-btn" style={{ display: "inline-flex", alignItems: "center", gap: "8px", textDecoration: "none" }}>
                  Read Full Standard Guide <FaArrowRight />
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="section-header-centered" style={{ marginBottom: "30px" }}>
          <h3>Verified Standards Index</h3>
          <div className="divider-line"></div>
        </div>

        <div className="blog-cards-display-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: "25px" }}>
          {currentCerts.length > 0 ? (
            currentCerts.map((item) => (
              <Link 
                key={item.id} 
                to={`/certifications/${item.id}`}
                className="blog-portal-card"
                style={{ background: "#ffffff", borderRadius: "12px", border: "1px solid #e2e8f0", overflow: "hidden", textDecoration: "none", display: "flex", flexDirection: "column", transition: "transform 0.3s ease, box-shadow 0.3s ease" }}
              >
                <div style={{ position: "relative", height: "200px" }}>
                  <img src={item.mainImage} alt={item.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  <span style={{ position: "absolute", top: "12px", left: "12px", background: "#1e3a8a", color: "#facc15", padding: "4px 10px", borderRadius: "4px", fontSize: "0.75rem", fontWeight: "700" }}>{item.category || "Standard"}</span>
                </div>
                <div style={{ padding: "20px", display: "flex", flexDirection: "column", flexGrow: "1", justifyContent: "space-between" }}>
                  <div>
                    <h4 style={{ fontSize: "1.2rem", fontWeight: "800", color: "#1e3a8a", marginBottom: "8px" }}>{item.title}</h4>
                    <h5 style={{ fontSize: "0.95rem", fontWeight: "600", color: "#0f172a", marginBottom: "10px" }}>{item.name}</h5>
                    <p style={{ fontSize: "0.9rem", color: "#475569", lineHeight: "1.5", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{item.purpose}</p>
                  </div>
                  <div style={{ marginTop: "15px", paddingTop: "12px", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: "0.85rem", fontWeight: "700", color: "#b91c1c" }}>View Guide</span>
                    <FaArrowRight style={{ color: "#b91c1c" }} />
                  </div>
                </div>
              </Link>
            ))
          ) : (
            <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "4rem 0" }}>
              <FaInfoCircle style={{ fontSize: "3rem", color: "#64748b", marginBottom: "1rem" }} />
              <h3>No Standards Located</h3>
              <p>Refine your search terms or switch the category filter tab.</p>
            </div>
          )}
        </div>

        {regularCerts.length > itemsPerPage && (
          <nav className="blog-pagination" style={{ display: "flex", justifyContent: "center", gap: "8px", marginTop: "40px" }}>
            <button
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={currentPage === 1}
              style={{ padding: "8px 16px", borderRadius: "6px", border: "1px solid #cbd5e1", background: "#fff", cursor: "pointer" }}
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
              <button
                key={page}
                className={currentPage === page ? "active" : ""}
                onClick={() => setCurrentPage(page)}
                style={{ padding: "8px 14px", borderRadius: "6px", border: "1px solid #cbd5e1", background: currentPage === page ? "#1e3a8a" : "#fff", color: currentPage === page ? "#fff" : "#334155", cursor: "pointer" }}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              disabled={currentPage === totalPages}
              style={{ padding: "8px 16px", borderRadius: "6px", border: "1px solid #cbd5e1", background: "#fff", cursor: "pointer" }}
            >
              Next
            </button>
          </nav>
        )}
      </main>

      {/* Newsletter / Consultation Dock */}
      <section className="cta-newsletter-box" style={{ maxWidth: "1320px", margin: "60px auto 40px auto" }}>
        <div className="cta-content">
          <h3>Subscribe to Compliance Briefings</h3>
          <p>Get immediate operational updates, audit circulars, and framework risk analysis worksheets directly inside your inbox.</p>
          <form className="cta-form" onSubmit={handleSubscribe} style={{ marginTop: "20px" }}>
            <div style={{ display: "flex", gap: "10px", maxWidth: "500px", margin: "0 auto" }}>
              <input 
                type="email" 
                placeholder="Enter corporate email address..." 
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                required
                style={{ flexGrow: 1, padding: "12px", borderRadius: "6px", border: "1px solid rgba(255,255,255,0.3)", background: "rgba(255,255,255,0.1)", color: "#fff", outline: "none" }}
              />
              <button type="submit" className="primary-action-btn" style={{ padding: "12px 20px" }}>
                Join Registry
              </button>
            </div>
          </form>
        </div>
      </section>

      <div className="newspaper-bottom-border"></div>
    </div>
  );
}

export default CertificationsListAndDetail;